import React from 'react';
import { Bell, AlertTriangle, Bug, Shield, UserX, HeadingIcon as PhishingIcon } from 'lucide-react';
import { AlertaSeguridad } from '../../types';

interface AlertListProps {
  alertas: AlertaSeguridad[];
}

const AlertList: React.FC<AlertListProps> = ({ alertas }) => {
  // Ordenar alertas por fecha (más recientes primero) y nivel de criticidad
  const alertasOrdenadas = [...alertas].sort((a, b) => {
    // Primero por nivel de criticidad
    const nivelOrden: Record<string, number> = { 'critico': 0, 'alto': 1, 'medio': 2, 'bajo': 3 };
    if (nivelOrden[a.nivel] !== nivelOrden[b.nivel]) {
      return nivelOrden[a.nivel] - nivelOrden[b.nivel];
    }
    // Luego por fecha (más recientes primero)
    return b.fecha.getTime() - a.fecha.getTime();
  });

  // Función para obtener el icono según el tipo de alerta
  const getAlertIcon = (tipo: string) => {
    switch (tipo) {
      case 'intrusion':
        return <Shield className="h-5 w-5 text-cyber-alert" />;
      case 'vulnerabilidad':
        return <Bug className="h-5 w-5 text-cyber-warning" />;
      case 'malware':
        return <AlertTriangle className="h-5 w-5 text-cyber-warning" />;
      case 'phishing':
        return <PhishingIcon className="h-5 w-5 text-cyber-alert" />;
      case 'acceso_no_autorizado':
        return <UserX className="h-5 w-5 text-cyber-alert" />;
      default:
        return <Bell className="h-5 w-5 text-cyber-blue" />;
    }
  };

  // Función para obtener el color de fondo según el nivel
  const getAlertBgColor = (nivel: string) => {
    switch (nivel) {
      case 'critico':
        return 'bg-cyber-alert/20 border-cyber-alert/50';
      case 'alto':
        return 'bg-cyber-alert/10 border-cyber-alert/30';
      case 'medio':
        return 'bg-cyber-warning/10 border-cyber-warning/30';
      case 'bajo':
        return 'bg-cyber-blue/10 border-cyber-blue/30';
      default:
        return 'bg-cyber-gray/10 border-cyber-gray/30';
    }
  };

  // Función para formatear la fecha
  const formatDate = (date: Date) => {
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-cyber-dark-blue bg-opacity-80 backdrop-blur-md p-4 rounded-lg border border-cyber-blue/30">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <Bell className="h-5 w-5 mr-2 text-cyber-blue" />
        Alertas de Seguridad
      </h3>
      
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
        {alertasOrdenadas.map((alerta) => (
          <div 
            key={alerta.id}
            className={`p-3 rounded-lg border ${getAlertBgColor(alerta.nivel)} transition-all duration-200 hover:bg-opacity-30`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-1">
                {getAlertIcon(alerta.tipo)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-1">
                  <h4 className="text-sm font-medium text-white truncate pr-2">{alerta.mensaje}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    alerta.estado === 'activa' 
                      ? 'bg-cyber-alert/20 text-cyber-alert' 
                      : alerta.estado === 'investigando' 
                        ? 'bg-cyber-warning/20 text-cyber-warning' 
                        : 'bg-cyber-safe/20 text-cyber-safe'
                  }`}>
                    {alerta.estado === 'activa' 
                      ? 'Activa' 
                      : alerta.estado === 'investigando' 
                        ? 'En investigación' 
                        : 'Resuelta'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <p className="text-xs text-cyber-light">{alerta.origen || 'Sistema'}</p>
                  <p className="text-xs text-cyber-light">{formatDate(alerta.fecha)}</p>
                </div>
                
                {alerta.detalles && (
                  <p className="text-xs text-cyber-light mt-1 line-clamp-2">
                    {alerta.detalles}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-3 text-center">
        <button className="text-xs text-cyber-blue hover:text-cyber-blue/80 transition-colors">
          Ver todas las alertas
        </button>
      </div>
    </div>
  );
};

export default AlertList;